import React, { useState, useCallback, useEffect, useRef } from 'react';
import s from './s.module.less';
import { Button, Alert, message, Image, Modal } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import AIRichText from 'components/AIRichText';
import AttachFiles, { isImage } from 'components/Prescriptions/components/AttachFiles';
import { submitTreatmentAddendum, getTreatmentAddendumList } from 'api/treatment';
import { getProviderPrivateFileSigned } from 'api/common';
import { EAIRichTextEditorType } from 'types/ai';
import { FileUploadResult } from 'hooks/useFileUpload';
import { ITreatmentAddendumItem } from 'types/treatment';
import { TextVisitAttachment } from 'types/textVisit';
import { formatUTCTime } from 'utils/common';
import { sanitizeRichHtml } from 'utils/sanitize';
import { getProviderNameDisplay } from 'utils/provider';
import ProviderStore from 'store/Provider';
import ProviderSignature from 'components/ProviderSignature';

type AttachmentWithPreview = TextVisitAttachment & { previewUrl?: string };

const PAGE_SIZE = 3;

interface AddendumsSectionProps {
    treatmentId: number;
    businessId: number;
}

const AddendumsSection = ({ treatmentId, businessId }: AddendumsSectionProps) => {
    const [getUser] = ProviderStore.useStore();
    const providerName = getProviderNameDisplay(getUser('data'));
    const providerCredentials = getUser('data')?.credential;

    const [isEditing, setIsEditing] = useState(false);
    const [content, setContent] = useState('');
    const [attachments, setAttachments] = useState<FileUploadResult[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [addendumList, setAddendumList] = useState<ITreatmentAddendumItem[]>([]);
    const [attachmentPreviewMap, setAttachmentPreviewMap] = useState<Record<number, AttachmentWithPreview[]>>({});
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    const editCardRef = useRef<HTMLDivElement>(null);

    const fetchAttachmentPreviews = useCallback(async (items: ITreatmentAddendumItem[]) => {
        const itemsWithAttachments = items.filter((item) => item.attachments?.length > 0);
        if (itemsWithAttachments.length === 0) return;

        const previewMap: Record<number, AttachmentWithPreview[]> = {};
        await Promise.all(
            itemsWithAttachments.map(async (item) => {
                const previews = await Promise.all(
                    item.attachments.map(async (attachment) => {
                        const res = await getProviderPrivateFileSigned(attachment.path || '');
                        return {
                            ...attachment,
                            previewUrl: !res.error ? res.data?.data?.singedUrl || '' : '',
                        };
                    }),
                );
                previewMap[item.id] = previews;
            }),
        );
        setAttachmentPreviewMap((prev) => ({ ...prev, ...previewMap }));
    }, []);

    const fetchAddendumList = useCallback(async () => {
        const res = await getTreatmentAddendumList(treatmentId);
        if (!res.error) {
            const items = res.data?.data || [];
            setAddendumList(items);
            fetchAttachmentPreviews(items);
        }
    }, [treatmentId, fetchAttachmentPreviews]);

    useEffect(() => {
        if (treatmentId) {
            fetchAddendumList();
        }
    }, [treatmentId, fetchAddendumList]);

    const handleSubmit = useCallback(async () => {
        setSubmitting(true);
        try {
            const res = await submitTreatmentAddendum({
                textVisitTreatmentId: treatmentId,
                content,
                attachments: attachments.map((file) => ({
                    name: file.name || '',
                    path: file.url || '',
                    type: file.type || '',
                })),
            });
            if (!res.error) {
                message.success('Addendum submitted successfully');
                setIsEditing(false);
                setContent('');
                setAttachments([]);
                fetchAddendumList();
            }
        } finally {
            setSubmitting(false);
        }
    }, [content, attachments, treatmentId, fetchAddendumList]);

    const handleCancel = () => {
        setIsEditing(false);
        setContent('');
        setAttachments([]);
    };

    const visibleItems = addendumList.slice(0, visibleCount);
    const hasMore = addendumList.length > visibleCount;

    return (
        <>
            <Alert
                type="info"
                showIcon
                icon={
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11V14C9 14.5523 9.44772 15 10 15H11C11.5523 15 12 14.5523 12 14C12 13.4477 11.5523 13 11 13V10C11 9.44772 10.5523 9 10 9H9Z"
                            fill="#60A5FA"
                        />
                    </svg>
                }
                message="Please use an addendum for any updates, corrections, or new details. Note that this will be visible to the patient immediately upon submission."
                className={s.alertInfo}
            />
            <div className={s.section}>
                <div className={s.sectionTitle}>Addendums</div>
                <div className={s.sectionContent}>
                    <Button
                        onClick={() => {
                            setIsEditing(true);
                            setTimeout(() => {
                                editCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                            }, 100);
                        }}
                        icon={<PlusOutlined />}
                        disabled={isEditing}
                    >
                        Add addendum
                    </Button>
                </div>
            </div>
            {visibleItems.length > 0 && (
                <div className={s.addendumList}>
                    {visibleItems.map((item) => (
                        <div key={item.id} className={s.addendumItem}>
                            <div className={s.addendumHeader}>
                                <span>
                                    {providerName}, {providerCredentials}
                                </span>
                                <span> added on </span>
                                {formatUTCTime(item.createdAt, 'MM/DD/YYYY, hh:mm A')}
                            </div>
                            <div className={s.addendumDivider} />
                            <div className={s.addendumContent} dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(item.content || '') }} />
                            {attachmentPreviewMap[item.id]?.length > 0 && (
                                <div className={s.addendumAttachments}>
                                    {attachmentPreviewMap[item.id].map((attachment, idx) =>
                                        isImage(attachment as FileUploadResult) ? (
                                            <Image
                                                key={idx}
                                                src={attachment.previewUrl}
                                                alt={attachment.name}
                                                className={s.attachmentImage}
                                                width={140}
                                            />
                                        ) : (
                                            <div
                                                key={idx}
                                                className={s.attachmentFile}
                                                onClick={() => window.open(attachment.previewUrl || '', '_blank')}
                                            >
                                                <div className={s.attachmentFileName}>{attachment.name || ''}</div>
                                                <div className={s.attachmentFileType}>
                                                    {attachment.name?.split('.').pop()?.toUpperCase() || 'File'}
                                                </div>
                                            </div>
                                        ),
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                    {hasMore && (
                        <Button className={s.showMoreBtn} onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}>
                            Show more
                        </Button>
                    )}
                </div>
            )}
            {isEditing && (
                <div className={s.editCard} ref={editCardRef}>
                    <div className={s.editCardTitle}>New addendum</div>
                    <AIRichText
                        onChange={setContent}
                        value={content}
                        relationId={businessId}
                        placeholder="Please enter any supplemental notes, updates, or information you'd like to add for this patient…"
                        type={EAIRichTextEditorType.TREATMENT_PLAN_ADDENDUM_GENERATE}
                        height="200px"
                        onFetchingUpdate={setIsFetching}
                    />
                    <AttachFiles onFilesChange={setAttachments} accept="image/*,application/pdf" />
                    <div className={s.editActions}>
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => {
                                if (!content && attachments.length === 0) {
                                    message.warning('Please enter addendum content or attach files');
                                    return;
                                }
                                setShowSubmitModal(true);
                            }}
                            loading={submitting}
                            disabled={isFetching}
                            className={s.smButton}
                        >
                            Submit
                        </Button>
                        <Button size="small" onClick={handleCancel} className={s.smButton}>
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
            <Modal
                open={showSubmitModal}
                onCancel={() => setShowSubmitModal(false)}
                okText="Sign & Send"
                onOk={() => {
                    if (!hasSignature) {
                        message.warning('Please add your signature before submitting.');
                        return;
                    }
                    setShowSubmitModal(false);
                    handleSubmit();
                }}
                width={512}
            >
                <div className="flex gap-4">
                    <div className={s.submitModalIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M8.68387 13.3419C8.88616 12.9381 9 12.4824 9 12C9 11.5176 8.88616 11.0619 8.68387 10.6581M8.68387 13.3419C8.19134 14.3251 7.17449 15 6 15C4.34315 15 3 13.6569 3 12C3 10.3431 4.34315 9 6 9C7.17449 9 8.19134 9.67492 8.68387 10.6581M8.68387 13.3419L15.3161 16.6581M8.68387 10.6581L15.3161 7.34193M15.3161 7.34193C15.8087 8.32508 16.8255 9 18 9C19.6569 9 21 7.65685 21 6C21 4.34315 19.6569 3 18 3C16.3431 3 15 4.34315 15 6C15 6.48237 15.1138 6.93815 15.3161 7.34193ZM15.3161 16.6581C15.1138 17.0619 15 17.5176 15 18C15 19.6569 16.3431 21 18 21C19.6569 21 21 19.6569 21 18C21 16.3431 19.6569 15 18 15C16.8255 15 15.8087 15.6749 15.3161 16.6581Z"
                                stroke="#0284C7"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <div className="flex flex-col gap-2">
                        <div className="text-gray-900 text-lg font-medium">Submit addendum to patient</div>
                        <div className="text-gray-500 text-sm">
                            Please review before submitting. After submission, the addendum will be{' '}
                            <span className="font-bold">signed and sent to the patient immediately</span>. It will also
                            be recorded under your name with a timestamp.
                        </div>
                    </div>
                </div>
                <ProviderSignature onSignatureStatusChange={setHasSignature} />
            </Modal>
        </>
    );
};

export default AddendumsSection;
